// Full-document render of the tempurpedic ProductComparePage: PageSkeleton (the
// React document shell) wrapping the body -- the whole `modules` field
// (ProductComparePageBodyContent) inside the
// `<section class="product-compare-page-block">` its shared Meta.template emits
// (no per-block w-block-* wrapper). The change-selection "add/change" link
// that product-compare.ts binds via a `.product-compare-page-block > a`
// direct-child selector renders inside the compare grid, a direct child of this
// section -- keep it that way.
import { Immutable } from "immer";
import React from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductComparePrintOnlyBlock } from "tsi-common-react/src/components/ProductComparePrintOnlyBlock/index";

import { templates } from "@reactivated";
import type { RichTextBlock as RichTextBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ToolboxPageSSR, buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the in-tree
// SiteHeader chain, typed with mutable reactivated props; the block components
// accept Immutable props, to which these are assignable.
/**
 * The page-derived SSR side-channels the `modules` component map closes over
 * (only what `buildToolboxPageMap` needs -- the three ProductComparePageContent
 * keys below are page-independent). Split out so the styleguide block-render
 * registry can build the same map with minimal (zeroed) props.
 */
type IMapProps = Immutable<ToolboxPageSSR>;

// ProductComparePageBodyContent = ProductComparePageContent + Toolbox, so the
// map is the PAGE-AWARE toolbox map (get_toolbox_block_ssr side-channels, so
// any Toolbox block an editor drops in `modules` renders with real props --
// NOT the base map's null defaults) plus the three ProductComparePageContent
// keys. `richtext` / `print_only_richtext` / `compare_grid` are pure additions
// (the toolbox key is `rich_text` with an underscore; there is no
// `compare_grid` / `print_only_richtext` in the toolbox map).
export const buildProductComparePageComponentsMap = (props: IMapProps) => {
    const componentsMap = {
        ...buildToolboxPageMap(props),
        richtext: (block: { value: Immutable<RichTextBlockValue> }) => (
            <RichTextBlock value={block.value} />
        ),
        // The block's get_data_for_client_render returns the expanded richtext
        // HTML string; the component supplies its own `.print-only-block` wrapper.
        print_only_richtext: ProductComparePrintOnlyBlock,
        // Same block / SSR envelope as the toolbox `product_compare_grid` key
        // (different key name); render it identically to that entry and the
        // standalone ProductCompareGridBlockTemplate -- `<ProductCompareGridSSR
        // {...} showFirstAvailablePrice />` (no `isMinimal`, a stearns-only prop).
        compare_grid: (block: {
            value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
        }) =>
            block.value ? (
                <ProductCompareGridSSR
                    {...block.value}
                    showFirstAvailablePrice
                />
            ) : null,
    } as const;
    return componentsMap;
};

export const Template = (
    props: templates.ProductComparePageTemplate,
): React.JSX.Element => {
    const componentsMap = buildProductComparePageComponentsMap(props);
    const sectionRef = React.useRef<HTMLElement>(null);
    // Persist the current selection to localStorage (the `selectedTileIDs` key,
    // <=4 length guard) when the change-selection "add/change" link -- a
    // direct-child `<a>` of the section -- is clicked. The store is dynamically
    // imported so this stays client-only (store.ts is not SSR-safe). NOTE: the
    // current React compare grid (ProductCompareGridSSR) renders its root as a
    // CSS-module `<div>`, so no direct-child `<a>` of the section exists and this
    // handler is presently inert -- preserved for parity, keyed on the
    // `.product-compare-page-block > a` selector.
    React.useEffect(() => {
        const section = sectionRef.current;
        if (!section) {
            return;
        }
        const onClick = (event: MouseEvent): void => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const link = target.closest("a");
            if (!link || link.parentElement !== section) {
                return;
            }
            void import("../store").then(({ store }) => {
                const win = getWindow();
                const selectedTileIDs =
                    store.getState().productcompare.selectedTileIDs;
                if (selectedTileIDs.length <= 4 && win?.localStorage) {
                    win.localStorage.setItem(
                        "selectedTileIDs",
                        JSON.stringify(selectedTileIDs),
                    );
                }
            });
        };
        section.addEventListener("click", onClick);
        return () => {
            section.removeEventListener("click", onClick);
        };
    }, []);
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <section className="product-compare-page-block" ref={sectionRef}>
                <StreamField
                    tagName={React.Fragment}
                    value={props.page.modules.value}
                    components={componentsMap}
                />
            </section>
        </PageSkeleton>
    );
};
